import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Clock, Search, UserPlus, Users as UsersIcon, UsersRound } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Gate } from '../components/shell/gate.tsx';
import { Badge } from '../components/ui/badge.tsx';
import { Button } from '../components/ui/button.tsx';
import { Card, CardDescription, CardHeader, CardTitle } from '../components/ui/card.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog.tsx';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu.tsx';
import { EmptyState } from '../components/ui/empty-state.tsx';
import { Input } from '../components/ui/input.tsx';
import { PageHeader } from '../components/ui/page-header.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.tsx';
import { CheckList } from '../components/record-table/option-search.tsx';
import { useDebounced } from '../components/record-table/use-debounced.ts';
import { describeError } from '../lib/api.ts';
import { api, type MemberView, type RoleView, type TeamView } from '../lib/client.ts';
import { useSession } from '../lib/session.tsx';

const STATUS_VARIANT = { active: 'success', invited: 'warning', deactivated: 'danger' } as const;
const STATUS_LABEL = { active: 'Active', invited: 'Waiting', deactivated: 'Deactivated' } as const;

/** Roles a person can hold: every role but Integration, which belongs to API keys. */
const assignable = (roles: readonly RoleView[]) => roles.filter((r) => r.key !== 'integration');

const initials = (m: MemberView) =>
  (m.name && m.name !== m.email ? m.name : m.email)
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');

/**
 * Settings → Users (TS-956): who is in the organisation, with what role. Admins invite people,
 * activate the ones waiting (a first sign-in from the company domain arrives as a waiting
 * Viewer), change roles and deactivate. Every control mirrors a server check: the API refuses
 * anything the caller may not do (and anything granting more than they hold), so the page only
 * hides what would fail anyway.
 */
export function UsersPage() {
  const session = useSession();
  const qc = useQueryClient();
  const org = session.me.organisation.id;
  const canManage = session.can('user.manage');
  const canInvite = session.can('user.invite');
  const members = useQuery({ queryKey: ['members', org], queryFn: api.users });
  const roles = useQuery({
    queryKey: ['roles', org],
    queryFn: api.roles.list,
    enabled: session.can('role.read'),
  });
  const canManageTeams = session.can('team.manage');
  const teams = useQuery({
    queryKey: ['teams', org],
    queryFn: api.teams.list,
    enabled: session.can('team.read'),
  });
  const roleName = useMemo(
    () => new Map((roles.data ?? []).map((r) => [r.key, r.name] as const)),
    [roles.data],
  );
  const [query, setQuery] = useState('');
  const [inviting, setInviting] = useState(false);
  const [confirm, setConfirm] = useState<MemberView | null>(null);
  const [reassigning, setReassigning] = useState<MemberView | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ['members', org] });
  const onError = (err: unknown) => setError(describeError(err));
  const activate = useMutation({
    mutationFn: (m: MemberView) => api.members.activate(m.id),
    onSuccess: async (m) => {
      setError(null);
      setNotice(`${m.name} can use ReAct now.`);
      await refresh();
    },
    onError,
  });
  const deactivate = useMutation({
    mutationFn: (m: MemberView) => api.members.deactivate(m.id),
    onSuccess: async (m) => {
      setConfirm(null);
      setError(null);
      setNotice(`${m.name} is deactivated and signed out everywhere.`);
      await refresh();
    },
    onError,
  });
  const setRole = useMutation({
    mutationFn: (v: { m: MemberView; role: string }) => api.members.setRole(v.m.id, v.role),
    onSuccess: async (m) => {
      setError(null);
      setNotice(`${m.name} is now ${roleName.get(m.role) ?? m.role}.`);
      await refresh();
    },
    onError,
  });

  // One call per ticked or unticked team, the same calls the Teams page makes.
  const setTeam = useMutation({
    mutationFn: (v: { m: MemberView; team: TeamView; on: boolean }) =>
      v.on ? api.teams.addMember(v.team.id, v.m.id) : api.teams.removeMember(v.team.id, v.m.id),
    onSuccess: async (_, v) => {
      setError(null);
      setNotice(`${v.m.name} ${v.on ? 'joined' : 'left'} ${v.team.name}.`);
      await qc.invalidateQueries({ queryKey: ['teams', org] });
    },
    onError,
  });

  const reassign = useMutation({
    mutationFn: (v: { from: MemberView; to: MemberView }) =>
      api.members.reassign(v.from.id, v.to.id),
    onSuccess: async (moved, v) => {
      setReassigning(null);
      setError(null);
      setNotice(
        `Moved ${plural(moved.projects, 'project')}, ${plural(moved.responsibilities ?? 0, 'project function')} and ${plural(moved.tasks, 'open task')} from ${v.from.name} to ${v.to.name}.`,
      );
      await Promise.all(
        ['tasks', 'project', 'project-history', 'records', 'activities'].map((key) =>
          qc.invalidateQueries({ queryKey: [key] }),
        ),
      );
    },
  });

  const all = members.data ?? [];
  // Mirrors the API: user.reassign at scope all reaches anyone; a narrower scope, only within a
  // team I lead and only the work of someone whose role grants no more than mine.
  const myId = session.me.user.id;
  const canReassignAny = session.can('user.reassign');
  const reassignsAll = session.me.permissions.some(
    (g) => g.permission === 'user.reassign' && g.scope === 'all',
  );
  const active = useMemo(
    () => new Set((members.data ?? []).filter((x) => x.status === 'active').map((x) => x.id)),
    [members.data],
  );
  const reach = useMemo(() => {
    const byMember = new Map<string, Set<string>>();
    for (const t of teams.data ?? []) {
      if (!t.members.some((x) => x.userId === myId && x.isLead)) continue;
      const ids = t.members.map((x) => x.userId);
      for (const id of ids) {
        const set = byMember.get(id) ?? new Set<string>();
        ids.forEach((x) => set.add(x));
        byMember.set(id, set);
      }
    }
    return byMember;
  }, [teams.data, myId]);
  const withinMe = useMemo(() => {
    const byRole = new Map((roles.data ?? []).map((r) => [r.key, r] as const));
    const mine = new Map(session.me.permissions.map((g) => [g.permission, g.scope] as const));
    const rank = { owned: 1, team: 2, all: 3 } as const;
    return (roleKey: string) => {
      const role = byRole.get(roleKey);
      if (!role) return false;
      const parent = role.inherits ? byRole.get(role.inherits) : undefined;
      return [...role.grants, ...(parent?.grants ?? [])].every((g) => {
        const held = mine.get(g.permission);
        return !!held && rank[held] >= rank[g.scope];
      });
    };
  }, [roles.data, session.me.permissions]);
  const reachable = (m: MemberView): ReadonlySet<string> | null => {
    if (!canReassignAny) return new Set();
    if (reassignsAll) return null;
    return withinMe(m.role) ? (reach.get(m.id) ?? new Set()) : new Set();
  };
  const canReassign = (m: MemberView) => {
    const allowed = reachable(m);
    return allowed === null
      ? active.size > (active.has(m.id) ? 1 : 0)
      : [...allowed].some((id) => id !== m.id && active.has(id));
  };
  const receiversFor = (m: MemberView) => {
    const allowed = reachable(m);
    return all.filter((x) => x.id !== m.id && active.has(x.id) && (!allowed || allowed.has(x.id)));
  };
  const waiting = all.filter((m) => m.status === 'invited');
  const q = query.trim().toLowerCase();
  const shown = all.filter((m) => !q || `${m.name} ${m.email} ${m.role}`.toLowerCase().includes(q));

  return (
    <Gate permission="user.read">
      <PageHeader
        title="Users"
        description="Everyone in this organization and the role that decides what they can do."
        actions={
          canInvite && (
            <Button onClick={() => setInviting(true)} data-testid="invite-user">
              <UserPlus /> Invite
            </Button>
          )
        }
      />
      {notice && (
        <p
          className="bg-state-success-bg text-state-success-fg border-state-success-border mb-5 rounded-xl border px-4 py-3 text-sm"
          role="status"
        >
          {notice}
        </p>
      )}
      {error && (
        <p
          className="bg-state-danger-bg text-state-danger-fg border-state-danger-border mb-5 rounded-xl border px-4 py-3 text-sm"
          role="alert"
        >
          {error}
        </p>
      )}
      {members.isError && (
        <p className="text-danger text-sm" role="alert">
          {describeError(members.error)}
        </p>
      )}

      {waiting.length > 0 && (
        <Card className="border-state-warning-border mb-6" data-testid="waiting-members">
          <CardHeader className="pb-1">
            <div className="flex items-center gap-3">
              <span className="bg-state-warning-bg text-state-warning-fg grid size-9 place-items-center rounded-xl">
                <Clock className="size-[18px]" aria-hidden />
              </span>
              <div>
                <CardTitle>Waiting for activation ({waiting.length})</CardTitle>
                <CardDescription>
                  Signed in from the company domain; they see nothing until someone activates them.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <ul className="divide-border divide-y px-2 pt-3 pb-2">
            {waiting.map((m) => (
              <li key={m.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <Person m={m} />
                <Badge variant="outline">{roleName.get(m.role) ?? m.role}</Badge>
                {canManage && (
                  <Button
                    size="sm"
                    onClick={() => activate.mutate(m)}
                    disabled={activate.isPending}
                    data-testid={`activate-${m.email}`}
                  >
                    Activate
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm min-w-48 flex-1">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2" />
          <Input
            aria-label="Filter members"
            placeholder="Filter by name, email or role…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <span className="text-muted-foreground text-sm">
          {all.length} member{all.length === 1 ? '' : 's'}
        </span>
      </div>

      {members.data && shown.length === 0 ? (
        <EmptyState icon={UsersIcon} title="Nobody matches that filter." />
      ) : (
        <div className="bg-card border-border rounded-card shadow-card overflow-x-auto border">
          <table className="w-full min-w-[680px] text-left text-sm" data-testid="members">
            <thead className="text-muted-foreground bg-muted text-xs">
              <tr>
                <th className="px-4 py-3 font-semibold">Member</th>
                <th className="px-4 py-3 font-semibold">Role</th>
                {teams.data && <th className="px-4 py-3 font-semibold">Teams</th>}
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {shown.map((m) => {
                const self = m.id === session.me.user.id;
                return (
                  <tr
                    key={m.id}
                    className="border-border hover:bg-surface-hover/60 border-t transition-colors"
                    data-testid="member-row"
                  >
                    <td className="px-4 py-3">
                      <Person m={m} you={self} />
                    </td>
                    <td className="px-4 py-3">
                      {canManage && !self && m.status !== 'deactivated' && roles.data ? (
                        <Select
                          value={m.role}
                          onValueChange={(role) => role !== m.role && setRole.mutate({ m, role })}
                          disabled={setRole.isPending}
                        >
                          <SelectTrigger className="h-9 w-48" aria-label={`Role of ${m.email}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {assignable(roles.data).map((r) => (
                              <SelectItem key={r.key} value={r.key}>
                                {r.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="font-medium">{roleName.get(m.role) ?? m.role}</span>
                      )}
                    </td>
                    {teams.data && (
                      <td className="px-4 py-3">
                        <TeamsCell
                          m={m}
                          teams={teams.data}
                          editable={canManageTeams && m.status !== 'deactivated'}
                          busy={setTeam.isPending}
                          onToggle={(team, on) => setTeam.mutate({ m, team, on })}
                        />
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <Badge variant={STATUS_VARIANT[m.status]} data-testid={`status-${m.email}`}>
                        {STATUS_LABEL[m.status]}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {canReassign(m) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setReassigning(m)}
                          data-testid={`reassign-${m.email}`}
                        >
                          Reassign work
                        </Button>
                      )}
                      {canManage && !self && m.status !== 'active' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => activate.mutate(m)}
                          disabled={activate.isPending}
                        >
                          {m.status === 'deactivated' ? 'Reactivate' : 'Activate'}
                        </Button>
                      )}
                      {canManage && !self && m.status === 'active' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-danger hover:text-danger"
                          onClick={() => setConfirm(m)}
                          data-testid={`deactivate-${m.email}`}
                        >
                          Deactivate
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <InviteDialog
        open={inviting}
        onOpenChange={setInviting}
        roles={assignable(roles.data ?? [])}
        onInvited={async (m) => {
          setInviting(false);
          setError(null);
          setNotice(
            m.status === 'active'
              ? `${m.email} is in as ${roleName.get(m.role) ?? m.role}: they can sign in with Google now.`
              : `${m.email} is invited; an admin has to activate them before they can use ReAct.`,
          );
          await refresh();
        }}
      />

      {reassigning && (
        <ReassignDialog
          from={reassigning}
          receivers={receiversFor(reassigning)}
          busy={reassign.isPending}
          error={reassign.error ? describeError(reassign.error) : null}
          onCancel={() => {
            // Closing mid-request would drop its outcome; it stays open until the answer.
            if (reassign.isPending) return;
            reassign.reset();
            setReassigning(null);
          }}
          onConfirm={(to) => reassign.mutate({ from: reassigning, to })}
          onChange={() => {
            if (!reassign.isPending) reassign.reset();
          }}
        />
      )}

      <Dialog open={confirm !== null} onOpenChange={(open) => !open && setConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deactivate {confirm?.name}?</DialogTitle>
            <DialogDescription>
              They are signed out everywhere at once and cannot sign in again until someone
              reactivates them. Their records and history stay.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => confirm && deactivate.mutate(confirm)}
              disabled={deactivate.isPending}
              data-testid="confirm-deactivate"
            >
              Deactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Gate>
  );
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

// The same as the lists' quick filters: shorter text does not narrow anything yet.
const FILTER_DEBOUNCE_MS = 300;
const FILTER_MIN_CHARS = 3;

function ReassignDialog({
  from,
  receivers,
  busy,
  error,
  onCancel,
  onConfirm,
  onChange,
}: {
  from: MemberView;
  receivers: MemberView[];
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (to: MemberView) => void;
  /** The choice changed: a refusal about the previous one no longer applies. */
  onChange: () => void;
}) {
  const [toId, setToId] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [picking, setPicking] = useState(false);
  const [filter, setFilter] = useState('');
  const settled = useDebounced(filter.trim().toLowerCase(), FILTER_DEBOUNCE_MS);
  const q = settled.length >= FILTER_MIN_CHARS ? settled : '';
  const shown = q
    ? receivers.filter((r) => `${r.name} ${r.email}`.toLowerCase().includes(q))
    : receivers;
  const to = receivers.find((r) => r.id === toId);
  const errorLine = error && (
    <p className="text-danger text-sm" role="alert" data-testid="reassign-error">
      {error}
    </p>
  );
  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent>
        {confirming && to ? (
          <>
            <DialogHeader>
              <DialogTitle>
                Move all of {from.name}'s work to {to.name}?
              </DialogTitle>
              <DialogDescription>
                This cannot be undone. Every project {from.name} owns, every project function they
                hold and every task assigned to them that is not done moves to {to.name} at once.
                There is no way to move it back as it was: reassigning from {to.name} later would
                also take their own work.
              </DialogDescription>
            </DialogHeader>
            {errorLine}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConfirming(false)} disabled={busy}>
                Back
              </Button>
              <Button
                variant="destructive"
                onClick={() => onConfirm(to)}
                disabled={busy}
                data-testid="confirm-reassign"
              >
                Yes, move the work
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Reassign {from.name}'s work</DialogTitle>
              <DialogDescription>
                Every project they own, every project function they hold and every task assigned to
                them that is not done moves to the person you choose. Each change is recorded in the
                history.
              </DialogDescription>
            </DialogHeader>
            {/* modal: the dialog blocks wheel scrolling outside it, and this list is portalled. */}
            <Popover open={picking} onOpenChange={setPicking} modal>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-label="New owner"
                  aria-expanded={picking}
                  className="w-full justify-between font-normal"
                >
                  <span className={to ? 'truncate' : 'text-muted-foreground truncate'}>
                    {to ? to.name : 'Choose who takes over'}
                  </span>
                  <ChevronDown className="opacity-60" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-(--radix-popover-trigger-width) space-y-2 p-2">
                <Input
                  autoFocus
                  aria-label="Filter people"
                  placeholder="Filter by name or email (3+ letters)…"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
                {shown.length === 0 ? (
                  <p className="text-muted-foreground px-2 text-sm">Nobody matches that filter.</p>
                ) : (
                  <CheckList
                    label="People"
                    items={shown.map((r) => ({ value: r.id, label: r.name }))}
                    selected={toId ? [toId] : []}
                    single
                    onToggle={(id) => {
                      setToId(id);
                      setPicking(false);
                      onChange();
                    }}
                  />
                )}
              </PopoverContent>
            </Popover>
            {errorLine}
            <DialogFooter>
              <Button variant="ghost" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                onClick={() => setConfirming(true)}
                disabled={!to}
                data-testid="review-reassign"
              >
                Continue
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TeamsCell({
  m,
  teams,
  editable,
  busy,
  onToggle,
}: {
  m: MemberView;
  teams: TeamView[];
  editable: boolean;
  /** A change is in flight: the ticks only update once it lands, so no second click. */
  busy: boolean;
  onToggle: (team: TeamView, on: boolean) => void;
}) {
  const mine = teams.filter((t) => t.members.some((x) => x.userId === m.id));
  const label = mine.length ? mine.map((t) => t.name).join(', ') : 'No team';
  if (!editable) return <span className="text-muted-foreground">{label}</span>;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="max-w-56 justify-start"
          aria-label={`Teams of ${m.email}`}
        >
          <UsersRound />
          <span className="truncate">{label}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto">
        {teams.length === 0 && (
          <DropdownMenuItem disabled>No teams yet: create one on the Teams page.</DropdownMenuItem>
        )}
        {teams.map((t) => (
          <DropdownMenuCheckboxItem
            key={t.id}
            checked={mine.includes(t)}
            disabled={busy}
            // Keep the menu open: putting someone in several teams is the usual case.
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={(on) => onToggle(t, on)}
          >
            {t.name}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Person({ m, you = false }: { m: MemberView; you?: boolean }) {
  return (
    <span className="flex min-w-0 flex-1 items-center gap-3">
      <span className="bg-brand-soft text-brand-soft-foreground grid size-9 shrink-0 place-items-center rounded-full text-xs font-bold">
        {initials(m)}
      </span>
      <span className="min-w-0">
        <span className="block truncate font-semibold">
          {m.name}
          {you && <span className="text-muted-foreground ml-1.5 font-normal">(you)</span>}
        </span>
        <span className="text-muted-foreground block truncate text-xs">{m.email}</span>
      </span>
    </span>
  );
}

function InviteDialog({
  open,
  onOpenChange,
  roles,
  onInvited,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roles: RoleView[];
  onInvited: (m: MemberView) => Promise<void>;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('viewer');
  const [error, setError] = useState<string | null>(null);
  const invite = useMutation({
    mutationFn: () =>
      api.members.invite({
        email: email.trim(),
        role,
        ...(name.trim() ? { name: name.trim() } : {}),
      }),
    onSuccess: async (m) => {
      setEmail('');
      setName('');
      setRole('viewer');
      setError(null);
      await onInvited(m);
    },
    onError: (err) => setError(describeError(err)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (email.trim()) invite.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>Invite someone</DialogTitle>
            <DialogDescription>
              They sign in with their Google Workspace account; the email has to match.
            </DialogDescription>
          </DialogHeader>
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            Work email
            <Input
              type="email"
              required
              autoFocus
              placeholder="name@remoteleverage.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-label="Work email"
            />
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            Name <span className="text-muted-foreground -mt-1 text-xs font-normal">optional</span>
            <Input value={name} onChange={(e) => setName(e.target.value)} aria-label="Name" />
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            Role
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger aria-label="Role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(roles.length ? roles : [{ key: 'viewer', name: 'Viewer' } as RoleView]).map(
                  (r) => (
                    <SelectItem key={r.key} value={r.key}>
                      {r.name}
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
          </label>
          {error && (
            <p className="text-danger text-sm" role="alert">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!email.trim() || invite.isPending}
              data-testid="send-invite"
            >
              <UserPlus /> Invite
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
